import React from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider, ColorSchemeScript } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { theme } from './app/theme';
import './app/i18n';
import { routeTree } from './routeTree.gen';
import { trpc, trpcClient } from './lib/trpc';
import { initSentry } from './lib/sentry';

// No-op when VITE_SENTRY_DSN is unset; safe to await before React renders.
void initSentry();

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/tiptap/styles.css';
import './app/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30 s stale window matches the cadence at which Meta-driven UI keys
      // can change without an explicit invalidation.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ColorSchemeScript defaultColorScheme="auto" />
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <ModalsProvider>
        <Notifications position="bottom-center" />
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-right" />}
          </QueryClientProvider>
        </trpc.Provider>
      </ModalsProvider>
    </MantineProvider>
  </React.StrictMode>,
);
